<template>
  <div
    ref="containerRef"
    class="zoomable-image-container"
    :class="{ 'is-zoomed': scale > 1, 'is-dragging': isDragging }"
    @wheel.prevent="handleWheel"
    @mousedown="handleMouseDown"
    @mousemove="handleMouseMove"
    @mouseup="handleMouseUp"
    @mouseleave="handleMouseLeave"
    @dblclick="resetZoom"
  >
    <img
      ref="imageRef"
      :src="src"
      :alt="alt"
      :class="imageClass"
      :style="computedImageStyle"
      loading="lazy"
      @load="handleImageLoad"
      @error="handleImageError"
    />
    <div class="image-controls">
      <AwButton
        variant="ghost"
        size="sm"
        aria-label="Dézoomer"
        :disabled="scale === 1"
        :leading-icon="mapIcon.zoom_out"
        @click="zoomOut"
      />
      <AwButton
        variant="ghost"
        size="sm"
        aria-label="Zoomer"
        :disabled="scale === 4"
        :leading-icon="mapIcon.zoom_in"
        @click="zoomIn"
      />
      <AwButton
        variant="ghost"
        size="sm"
        aria-label="Tourner à gauche"
        :leading-icon="mapIcon.rotate_left"
        @click="rotateLeft"
      />
      <AwButton
        variant="ghost"
        size="sm"
        aria-label="Tourner à droite"
        :leading-icon="mapIcon.rotate"
        @click="rotateRight"
      />
      <AwButton
        variant="ghost"
        size="sm"
        aria-label="Réinitialiser la rotation"
        :disabled="rotation === 0"
        :leading-icon="mapIcon.refresh"
        @click="resetRotation"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from "vue";
import { AwButton } from "@asap/design";
import { mapIcon } from "~/utils/icon";

const props = withDefaults(
  defineProps<{
    src: string;
    alt?: string;
    minHeight?: number;
    imageClass?: string;
    imageStyle?: Record<string, string>;
  }>(),
  {
    alt: "",
    minHeight: 760,
    imageClass: "",
    imageStyle: () => ({}),
  }
);

const emit = defineEmits<{
  load: [event: Event];
  error: [event: Event];
}>();

const containerRef = ref<HTMLDivElement | null>(null);
const imageRef = ref<HTMLImageElement | null>(null);

// Zoom state
const scale = ref(1);
const translateX = ref(0);
const translateY = ref(0);

// Drag state
const isDragging = ref(false);
const lastMouseX = ref(0);
const lastMouseY = ref(0);

// Image metadata
const imageMeta = ref<{ width: number; height: number } | null>(null);

// Rotation state
const rotation = ref(0);

// Reset zoom when image changes
watch(
  () => props.src,
  () => {
    resetZoom();
    resetRotation();
    imageMeta.value = null;
  }
);

// ESC key handler
function handleKeyDown(event: KeyboardEvent) {
  if (event.key === "Escape" && scale.value > 1) {
    resetZoom();
  }
}

onMounted(() => {
  window.addEventListener("keydown", handleKeyDown);
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleKeyDown);
});

function handleImageLoad(event: Event) {
  const imageElement = event.target as HTMLImageElement;
  imageMeta.value = {
    width: imageElement.naturalWidth,
    height: imageElement.naturalHeight,
  };
  emit("load", event);
}

function handleImageError(event: Event) {
  emit("error", event);
}

function resetZoom() {
  scale.value = 1;
  translateX.value = 0;
  translateY.value = 0;
}

function zoomIn() {
  scale.value = Math.min(4, scale.value + 0.5);
}

function zoomOut() {
  scale.value = Math.max(1, scale.value - 0.5);
  if (scale.value === 1) {
    translateX.value = 0;
    translateY.value = 0;
  }
}

function rotateLeft() {
  rotation.value = (((rotation.value - 90) % 360) + 360) % 360;
  resetZoom();
}

function rotateRight() {
  rotation.value = (rotation.value + 90) % 360;
  resetZoom();
}

function resetRotation() {
  rotation.value = 0;
  resetZoom();
}

function handleWheel(event: WheelEvent) {
  if (!containerRef.value || !imageRef.value) return;

  const rect = containerRef.value.getBoundingClientRect();
  const mouseX = event.clientX - rect.left;
  const mouseY = event.clientY - rect.top;

  // Calculate zoom delta
  const zoomSensitivity = 0.001;
  const delta = -event.deltaY * zoomSensitivity;
  const newScale = Math.max(1, Math.min(4, scale.value + delta));

  if (newScale === scale.value) return;

  // Calculate zoom point in image coordinates
  const imageX = (mouseX - translateX.value) / scale.value;
  const imageY = (mouseY - translateY.value) / scale.value;

  // Adjust translate to keep mouse point fixed
  translateX.value = mouseX - imageX * newScale;
  translateY.value = mouseY - imageY * newScale;

  scale.value = newScale;
}

function handleMouseDown(event: MouseEvent) {
  if (scale.value <= 1) return;

  isDragging.value = true;
  lastMouseX.value = event.clientX;
  lastMouseY.value = event.clientY;
  event.preventDefault();
}

function handleMouseMove(event: MouseEvent) {
  if (!isDragging.value || scale.value <= 1) return;

  const deltaX = event.clientX - lastMouseX.value;
  const deltaY = event.clientY - lastMouseY.value;

  translateX.value += deltaX;
  translateY.value += deltaY;

  lastMouseX.value = event.clientX;
  lastMouseY.value = event.clientY;
}

function handleMouseUp() {
  isDragging.value = false;
}

function handleMouseLeave() {
  isDragging.value = false;
}

const computedImageStyle = computed(() => {
  const baseStyle: Record<string, string> = {
    transform: `translate(${translateX.value}px, ${translateY.value}px) scale(${scale.value}) rotate(${rotation.value}deg)`,
    transformOrigin: "center center",
    transition: isDragging.value ? "none" : "transform 0.2s ease",
    userSelect: "none",
    ...props.imageStyle,
  };

  // Preserve original minHeight logic
  if (!imageMeta.value) {
    baseStyle.maxWidth = "100%";
    baseStyle.height = `${props.minHeight}px`;
  } else {
    const isPortrait = imageMeta.value.height > imageMeta.value.width;
    if (isPortrait) {
      baseStyle.maxWidth = "100%";
      baseStyle.height = `${props.minHeight}px`;
    } else {
      baseStyle.width = "100%";
      baseStyle.height = "auto";
    }
  }

  return baseStyle;
});
</script>

<style scoped lang="scss">
.zoomable-image-container {
  position: relative;
  overflow: hidden;
  width: 100%;
  cursor: default;

  &.is-zoomed {
    cursor: grab;

    &.is-dragging {
      cursor: grabbing;
    }
  }

  img {
    display: block;
    object-fit: contain;
    border-radius: 8px;
    border: 1px solid var(--border, #e5e7eb);
  }
}

.image-controls {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  gap: 8px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  padding: 8px;
  border-radius: 8px;
  box-shadow: var(--shadow-2);
  z-index: 10;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.zoomable-image-container:hover .image-controls {
  opacity: 1;
}
</style>
